import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

import { chartColors } from '../../helpers/chartColors';
// import { useTheme } from '@mui/styles';
import NoChartData from './NoChartData';

const RTFOUploadDataChart = ({ parsedData, channelMapping, darkMode, units }) => {
  // const theme = useTheme();

  const data = useMemo(() => {
    if (!parsedData || !parsedData?.length > 0) return [];

    return getChartData();

    function getChartData() {
      try {
        const X = [];
        const YPressure = [];
        const YRate = [];
        const YClean = [];
        const YProducedRate = [];
        const YPropConcSurf = [];
        const YPropConcBH = [];
        const YFR = [];
        const YFR2 = [];
        const YCrossLinker = [];
        const YGellant = [];
        const YTPRate = [];

        parsedData?.forEach((d, i) => {
          const timestamp = new Date(d[channelMapping['Date']]).getTime();

          if (timestamp > 0) {
            X.push(timestamp);
            YPressure.push(d[channelMapping['Pressure']] || 0);
            YRate.push(d[channelMapping['Slurry Rate']] || 0);
            YClean.push(d[channelMapping['Clean Rate']] || 0);
            YProducedRate.push(d[channelMapping['Produced Rate']] || 0);
            YPropConcSurf.push(d[channelMapping['Proppant Conc.']] || 0);
            YPropConcBH.push(d[channelMapping['BH Proppant Conc.']] || 0);
            YFR.push(d[channelMapping['DFR Conc.']] || 0);
            YFR2.push(d[channelMapping['LFR Conc.']] || 0);
            YCrossLinker.push(d[channelMapping['Cross-Linker Conc.']] || 0);
            YGellant.push(d[channelMapping['Gellant Conc.']] || 0);
            YTPRate.push(d[channelMapping['TP Rate']] || 0);
          }
        });

        // add traces
        let treatingPressureTrace = {
          x: X,
          y: YPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[2]
          },
          connectgaps: false
        };
        let rateTrace = {
          x: X,
          y: YRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[3]
          },
          connectgaps: false
        };
        let cleanTrace = {
          x: X,
          y: YClean,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: `Clean Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[3],
            dash: 'dot'
          },
          connectgaps: false
        };
        let producedRateTrace = {
          x: X,
          y: YProducedRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: `Produced Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[7]
          }
        };
        let tpRateTrace = {
          x: X,
          y: YTPRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `TP Rate (${units === 'field' ? 'bbl/min' : 'L/min'})`,
          line: {
            width: 2,
            color: chartColors[0]
          }
        };
        let propConcSurfTrace = {
          x: X,
          y: YPropConcSurf,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `Propp. Surf. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[4]
          }
        };
        let propConcBHTrace = {
          x: X,
          y: YPropConcBH,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `Propp. BH (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[6]
          }
        };
        let frConcTrace = {
          x: X,
          y: YFR,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `DFR Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dot'
          }
        };
        let fr2ConcTrace = {
          x: X,
          y: YFR2,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `LFR Conc. (${units === 'field' ? 'gal/mgal' : 'L/m3'})`,
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dash'
          }
        };
        let crossLinkerConcTrace = {
          x: X,
          y: YCrossLinker,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `Cross-Linker Conc. (${units === 'field' ? 'gal/mgal' : 'L/m3'})`,
          line: {
            width: 2,
            color: chartColors[9],
            dash: 'dashdot'
          }
        };
        let gellantConcTrace = {
          x: X,
          y: YGellant,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `Gellant Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[10],
            dash: 'dashdot'
          }
        };

        let traces = [
          treatingPressureTrace,
          rateTrace,
          cleanTrace,
          producedRateTrace,
          tpRateTrace,
          propConcSurfTrace,
          propConcBHTrace,
          frConcTrace,
          fr2ConcTrace,
          crossLinkerConcTrace,
          gellantConcTrace
        ];

        return traces;
      } catch (error) {
        console.log(error);
        return [];
      }
    }
  }, [channelMapping, parsedData, units]);

  const layout = {
    yaxis: {
      // Treating pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        font: {
          size: 12
        }
      },
      anchor: 'x',
      range: units === 'field' ? [0, 15000] : [0, 100],
      rangemode: 'nonnegative',
      side: 'left'
    },
    yaxis2: {
      // Slurry rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: `Slurry, Clean and Produced Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        font: {
          size: 12
        }
      },
      range: units === 'field' ? [0, 250] : [0, 25],
      rangemode: 'nonnegative',
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0
    },
    yaxis3: {
      // Prop conc surf y axis
      linecolor: chartColors[4],
      showgrid: false,
      color: chartColors[4],
      title: {
        text: `Propp. Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
        font: {
          size: 12
        }
      },
      range: [0, 1000],
      rangemode: 'nonnegative',
      overlaying: 'y',
      side: 'right',
      anchor: 'x'
    },
    yaxis4: {
      // FR conc y axis
      linecolor: chartColors[8],
      showgrid: false,
      color: chartColors[8],
      title: {
        text: `FR, Cross-Linker, Gellant Conc. (${units === 'field' ? 'lb/gal or gal/mgal' : 'kg/m3 or L/m3'})`,
        font: {
          size: 12
        }
      },
      range: [0, 10],
      rangemode: 'nonnegative',
      overlaying: 'y',
      side: 'right',
      anchor: 'free',
      position: 1,
      uirevision: 'true'
    },
    xaxis: {
      // Date x axis
      autorange: true,
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      domain: [0.1, 0.9],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: 'dot',
      spikecolor: '#999999',
      spikemode: 'across',
      type: 'date'
    },
    hovermode: 'x',
    hoverdistance: 20, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: 'h',
      yanchor: 'top',
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: -0.15
    },
    annotations: [],
    shapes: [],
    uirevision: 'true',
    margin: { t: 60, l: 60, r: 60, b: 60 },
    images: [
      {
        x: 0.05,
        y: 1.05,
        sizex: 0.1,
        sizey: 0.1,
        source: `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: 'page',
        yref: 'y1 domain',
        xanchor: 'right',
        yanchor: 'bottom',
        opacity: 0.75
      }
    ],
    plot_bgcolor: 'transparent',
    paper_bgcolor: 'transparent'
    // paper_bgcolor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: 'RTFO-' + new Date().toISOString().split('T')[0],
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return data?.length > 0 ? (
    <Plot data={data} layout={layout} config={config} useResizeHandler={true} style={{ width: '100%', height: '100%' }} />
  ) : (
    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <NoChartData />
    </div>
  );
};

export default RTFOUploadDataChart;